'use client'

import React from 'react'

import { RateStar, RatingStar } from './RatingStar'
import { Container, Gap, Info, Size } from './styles'

export interface RatingProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Rating value
   */
  value: number

  /**
   * Size of the stars
   */
  size?: Size

  /**
   * Gap between stars
   */
  gap?: Gap

  /**
   * Show rating value
   */
  displayValue?: boolean

  /**
   * Total rating count
   */
  totalCount?: number

  /**
   * Total rating count suffix
   */
  totalCountSuffix?: string

  /**
   * Is rating editable?
   */
  editable?: boolean

  /**
   * Rating change handler (Only works when `editable` is set to `true`)
   */
  onChange?: (value: number) => void
}

const STAR_COUNT = 5

const stars = Array.from({ length: STAR_COUNT }, (_, i) => i + 1) as RateStar[]

export const Rating = ({
  value = 0,
  size = 'default',
  gap = 'default',
  displayValue,
  totalCount,
  totalCountSuffix,
  editable,
  onChange,
}: RatingProps) => {
  const starCount = Math.floor(value)
  const [hoveredStar, setHoveredStar] = React.useState<RateStar | null>(null)

  const handleMouseEnter = React.useCallback((num: RateStar) => {
    setHoveredStar(num)
  }, [])

  const handleMouseLeave = React.useCallback(() => {
    setHoveredStar(null)
  }, [])

  const handleClick = React.useCallback(
    (num: RateStar) => {
      onChange?.(num === value ? 0 : num)
    },
    [onChange, value],
  )

  return (
    <Container size={size} gap={gap}>
      {stars.map((star) => (
        <RatingStar
          key={star}
          number={star}
          value={starCount}
          editable={editable}
          hovering={hoveredStar !== null && hoveredStar >= star}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      ))}

      {(displayValue || typeof totalCount === 'number') && (
        <Info>
          {displayValue && value.toFixed(1)}
          {typeof totalCount === 'number' && (
            <span>
              ({totalCount}
              {totalCountSuffix})
            </span>
          )}
        </Info>
      )}
    </Container>
  )
}
